import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, TextField, FormControl,
  InputLabel, Select, MenuItem, Alert, Snackbar, CircularProgress,
  Tooltip, Avatar, Grid, Card, CardContent
} from '@mui/material';
import {
  Edit, Delete, CheckCircle, Cancel, Search, FilterList,
  Refresh, Person, Store, Payment
} from '@mui/icons-material';
import axios from '../../utils/CustomAxios';
import { useDispatch, useSelector } from 'react-redux';
import { updatepageName } from '../../Redux/page';
import { useGetInventory } from '../globalFunctions';
import {
  GrowthPrimaryButton,
  GrowthSecondaryButton,
  growthDialogPaperSx,
  growthDialogTitleSx,
  growthDialogActionsSx,
  GROWTH_FONT,
} from '../growth_hub/GrowthModal';

const subscriptionMatchesHub = (subscription, hubId) => {
  if (!hubId) {
    return true;
  }
  const hubNum = Number(hubId);
  const invId = subscription?.inventory?.id ?? subscription?.inventory;
  if (invId != null && Number(invId) === hubNum) {
    return true;
  }
  const applicable = Array.isArray(subscription?.applicable_hub_ids)
    ? subscription.applicable_hub_ids.map(Number)
    : [];
  return applicable.includes(hubNum);
};

const formatHubLabel = (subscription) => {
  if (subscription?.inventory?.place) {
    return subscription.inventory.place;
  }
  if (subscription?.inventory_details?.place) {
    return subscription.inventory_details.place;
  }
  const applicable = Array.isArray(subscription?.applicable_hub_ids)
    ? subscription.applicable_hub_ids
    : [];
  if (applicable.length) {
    return `Hubs: ${applicable.join(', ')}`;
  }
  return 'All hubs';
};

const formatUpgradeLabel = (subscription) => {
  const prev = subscription?.previous_subscription_details;
  if (!prev?.plan_name) {
    return null;
  }
  const current = subscription?.subscription_plan?.plan_name || 'Current plan';
  return `${prev.plan_name} → ${current}`;
};

export function UserSubscriptions() {
  const dispatch = useDispatch();
  const inventory = useSelector((state) => state.inventory.inventory) || [];
  const getInventory = useGetInventory();
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    inventory_id: '',
    user_id: '',
    search: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [stats, setStats] = useState({ total: 0, by_hub: [], by_plan: [], by_hub_plan: [] });

  useEffect(() => {
    dispatch(updatepageName("user_subscriptions"));
    if (!inventory || inventory.length === 0) {
      getInventory();
    }
    fetchSubscriptions();
    fetchStats();
  }, [dispatch]);

  const hubOptions = useMemo(() => {
    const fromInventory = (inventory || []).map((hub) => ({
      id: hub.value ?? hub.id,
      place: hub.name || hub.place || `Hub ${hub.value ?? hub.id}`,
    })).filter((hub) => hub.id != null);

    if (fromInventory.length) {
      return fromInventory.sort((a, b) => String(a.place).localeCompare(String(b.place)));
    }

    const fromSubs = Array.from(
      new Map(
        subscriptions
          .map((sub) => {
            const id = sub.inventory?.id ?? sub.inventory_details?.id;
            const place = sub.inventory?.place ?? sub.inventory_details?.place;
            if (!id) return null;
            return [Number(id), { id: Number(id), place: place || `Hub ${id}` }];
          })
          .filter(Boolean)
      ).values()
    );
    return fromSubs.sort((a, b) => String(a.place).localeCompare(String(b.place)));
  }, [inventory, subscriptions]);

  const fetchStats = async () => {
    try {
      const response = await axios.get('subscriptions/stats/', { params: { status: 'active' } });
      setStats(response.data || { total: 0, by_hub: [], by_plan: [], by_hub_plan: [] });
    } catch (error) {
      console.error('Failed to fetch subscription stats', error);
    }
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const params = { scope: 'all' };
      if (filters.status) params.status = filters.status;
      if (filters.inventory_id) params.inventory_id = filters.inventory_id;

      const response = await axios.get('subscriptions/user/', { params });
      setSubscriptions(response.data);
      fetchStats();
    } catch (error) {
      showSnackbar('Failed to fetch user subscriptions', 'error');
    }
    setLoading(false);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleViewDetails = async (subscription) => {
    setSelectedSubscription(subscription);
    setOpen(true);
    setHistoryLoading(true);
    setSubscriptionHistory([]);
    try {
      const params = { scope: 'all' };
      if (subscription?.user?.id) {
        params.user_id = subscription.user.id;
      }
      const response = await axios.get('subscriptions/user/history/', { params });
      const rows = Array.isArray(response.data) ? response.data : [];
      const related = rows.filter(
        (row) =>
          row.user_subscription === subscription.id ||
          Number(row.metadata?.new_subscription_id) === Number(subscription.id) ||
          Number(row.metadata?.previous_subscription_id) === Number(subscription.id)
      );
      setSubscriptionHistory(related.length ? related : rows);
    } catch (error) {
      console.error('Failed to load subscription history', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedSubscription(null);
    setSubscriptionHistory([]);
  };

  const handleStatusChange = async (subscriptionId, newStatus) => {
    setStatusUpdatingId(subscriptionId);
    try {
      await axios.patch(`subscriptions/user/${subscriptionId}/`, {
        status: newStatus
      });
      showSnackbar(`Subscription status updated to ${newStatus}`);
      fetchSubscriptions();
    } catch (error) {
      showSnackbar('Failed to update subscription status', 'error');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleRefund = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to process a refund for this subscription?')) {
      return;
    }

    try {
      const response = await axios.post(`subscriptions/${subscriptionId}/refund/`, {
        reason: 'Admin initiated refund'
      });

      if (response.data.status === 'refunded') {
        showSnackbar('Refund processed successfully');
        fetchSubscriptions();
      } else {
        showSnackbar(response.data.message || 'Refund failed', 'error');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to process refund';
      showSnackbar(errorMessage, 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'warning';
      case 'cancelled': return 'error';
      case 'pending_payment': return 'info';
      case 'suspended': return 'default';
      default: return 'default';
    }
  };

  const formatDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 30) {
      return `${diffDays} days`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(diffDays / 365);
      return `${years} year${years > 1 ? 's' : ''}`;
    }
  };

  const selectedUserId = filters.user_id ? Number(filters.user_id) : null;
  const selectedHubId = filters.inventory_id ? Number(filters.inventory_id) : null;
  const hubUsers = subscriptions
    .filter(sub => subscriptionMatchesHub(sub, selectedHubId))
    .map(sub => sub.user)
    .filter((user, index, users) => user && users.findIndex(item => item?.id === user.id) === index);

  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesSearch = !filters.search ||
      sub.user?.first_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      sub.user?.last_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      sub.user?.mobileno?.includes(filters.search) ||
      sub.subscription_plan?.plan_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      sub.inventory?.place?.toLowerCase().includes(filters.search.toLowerCase()) ||
      sub.inventory_details?.place?.toLowerCase().includes(filters.search.toLowerCase());

    const matchesStatus = !filters.status || sub.status === filters.status;
    const matchesInventory = subscriptionMatchesHub(sub, selectedHubId);
    const matchesUser = !selectedUserId || sub.user?.id === selectedUserId;

    return matchesSearch && matchesStatus && matchesInventory && matchesUser;
  });
    

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          User Subscriptions Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchSubscriptions}
        >
          Refresh
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4} md={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Active subscribers</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.total || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        {(stats.by_hub || []).slice(0, 6).map((row) => (
          <Grid item xs={12} sm={4} md={3} key={`hub-${row.inventory_id || 'all'}`}>
            <Card>
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  Hub: {row.inventory__place || 'All hubs'}
                </Typography>
                <Typography variant="h5" fontWeight="bold">{row.count}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {(stats.by_plan || []).length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>By plan</Typography>
            <Grid container spacing={1}>
              {(stats.by_plan || []).map((row) => (
                <Grid item xs={12} sm={6} md={4} key={`plan-${row.subscription_plan_id}`}>
                  <Chip
                    label={`${row.subscription_plan__plan_name || 'Plan'}: ${row.count}`}
                    color="primary"
                    variant="outlined"
                    sx={{ width: '100%', justifyContent: 'space-between' }}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
            Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                placeholder="Search users, plans, or hubs..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel shrink>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  label="Status"
                  displayEmpty
                  renderValue={(selected) => (selected ? selected.replace('_', ' ') : 'All Status')}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="pending_payment">Pending Payment</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel shrink>Hub</InputLabel>
                {/* <Select
                  value={filters.inventory_id}
                  onChange={(e) => setFilters({...filters, inventory_id: e.target.value})}
                  label="Hub"
                >
                  <MenuItem value="">All Hubs</MenuItem>
                   {Array.from(new Set(subscriptions.map(s => s.inventory?.id).filter(Boolean))).map(inventoryId => {
                    const inventory = subscriptions.find(s => s.inventory?.id === inventoryId)?.inventory;
                    return (
                      <MenuItem key={inventoryId} value={inventoryId}>
                        {inventory?.place}
                      </MenuItem>
                    );
                  })} 
                </Select> */}
                <Select
                  value={filters.inventory_id}
                  onChange={(e) =>
                    setFilters({ ...filters, inventory_id: e.target.value, user_id: '' })
                  }
                  label="Hub"
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return 'All Hubs';
                    const hub = hubOptions.find(h => String(h.id) === String(selected));
                    return hub?.place || 'Selected Hub';
                  }}
                >
                  <MenuItem value="">All Hubs</MenuItem>
                  {hubOptions.map((hub) => (
                    <MenuItem key={hub.id} value={String(hub.id)}>
                      {hub.place}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel shrink>User</InputLabel>
                <Select
                  value={filters.user_id}
                  onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                  label="User"
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return 'All Users';
                    const user = hubUsers.find(u => String(u.id) === String(selected));
                    return user ? `${user.first_name} ${user.last_name} - ${user.mobileno}` : 'Selected User';
                  }}
                >
                  <MenuItem value="">All Users</MenuItem>
                {hubUsers.map(u => (
                  <MenuItem key={u.id} value={String(u.id)}>
                    {u.first_name} {u.last_name} - {u.mobileno}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => setFilters({ status: '', inventory_id: '', user_id: '', search: '' })}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : filteredSubscriptions.length === 0 ? (
        <Box textAlign="center" p={6}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No User Subscriptions Found
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {subscriptions.length === 0
              ? "No users have purchased subscriptions yet."
              : "No subscriptions match your current filters. Try adjusting your search criteria."
            }
          </Typography>
          {subscriptions.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Users can purchase subscriptions through the mobile app or checkout process.
            </Typography>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>User</strong></TableCell>
                <TableCell><strong>Plan</strong></TableCell>
                <TableCell><strong>Hub</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Amount</strong></TableCell>
                <TableCell><strong>Duration</strong></TableCell>
                <TableCell><strong>Start Date</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSubscriptions.map((subscription) => (
                <TableRow key={subscription.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ mr: 2, bgcolor: '#ED1E79' }}>
                        <Person />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {subscription.user?.first_name} {subscription.user?.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {subscription.user?.mobileno}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {subscription.subscription_plan?.plan_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {subscription.subscription_plan?.plan_type}
                    </Typography>
                    {formatUpgradeLabel(subscription) && (
                      <Chip
                        label={formatUpgradeLabel(subscription)}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ mt: 0.5, display: 'block', maxWidth: 180 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Store sx={{ mr: 1, color: 'action.active' }} />
                      <Typography variant="body2">
                        {formatHubLabel(subscription)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={subscription.status.replace('_', ' ')}
                      size="small"
                      color={getStatusColor(subscription.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="medium">
                      ₹{subscription.total_amount_paid}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {subscription.start_date && subscription.end_date &&
                      formatDuration(subscription.start_date, subscription.end_date)
                    }
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {subscription.start_date &&
                        new Date(subscription.start_date).toLocaleDateString()
                      }
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        color="primary"
                        onClick={() => handleViewDetails(subscription)}
                        size="small"
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>

                    {subscription.can_refund_subscription && (
                      <Tooltip title="Process Refund">
                        <IconButton
                          color="warning"
                          onClick={() => handleRefund(subscription.id)}
                          size="small"
                        >
                          <Payment />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Subscription Details Dialog */}
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: growthDialogPaperSx }}
      >
        <DialogTitle sx={growthDialogTitleSx}>
          Subscription Details
        </DialogTitle>
        <DialogContent sx={{ fontFamily: GROWTH_FONT }}>
          {selectedSubscription && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                      User Information
                    </Typography>
                    <Typography>
                      <strong>Name:</strong> {selectedSubscription.user?.first_name} {selectedSubscription.user?.last_name}
                    </Typography>
                    <Typography>
                      <strong>Mobile:</strong> {selectedSubscription.user?.mobileno}
                    </Typography>
                    <Typography>
                      <strong>Email:</strong> {selectedSubscription.user?.email}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <Store sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Plan & Hub Information
                    </Typography>
                    <Typography>
                      <strong>Plan:</strong> {selectedSubscription.subscription_plan?.plan_name}
                    </Typography>
                    <Typography>
                      <strong>Type:</strong> {selectedSubscription.subscription_plan?.plan_type}
                    </Typography>
                    <Typography>
                      <strong>Hub:</strong> {formatHubLabel(selectedSubscription)}
                    </Typography>
                    {formatUpgradeLabel(selectedSubscription) && (
                      <Typography>
                        <strong>Upgraded from:</strong> {formatUpgradeLabel(selectedSubscription)}
                      </Typography>
                    )}
                    <Typography>
                      <strong>Amount:</strong> ₹{selectedSubscription.total_amount_paid}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Subscription Status
                    </Typography>
                    <Typography>
                      <strong>Status:</strong>
                      <Chip
                        label={selectedSubscription.status.replace('_', ' ')}
                        size="small"
                        color={getStatusColor(selectedSubscription.status)}
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                    <Typography>
                      <strong>Upgrade Type:</strong> {String(selectedSubscription.upgrade_type || 'new').replace(/_/g, ' ')}
                    </Typography>
                    <Typography>
                      <strong>Start Date:</strong> {selectedSubscription.start_date &&
                        new Date(selectedSubscription.start_date).toLocaleDateString()}
                    </Typography>
                    <Typography>
                      <strong>End Date:</strong> {selectedSubscription.end_date &&
                        new Date(selectedSubscription.end_date).toLocaleDateString()}
                    </Typography>
                    {selectedSubscription.days_remaining !== undefined && (
                      <Typography>
                        <strong>Days Remaining:</strong> {selectedSubscription.days_remaining}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Benefits & Applied Offers
                    </Typography>
                    <Typography>
                      <strong>Applied Benefits:</strong>
                    </Typography>
                    {selectedSubscription.applied_benefits?.map((benefit, index) => (
                      <Chip
                        key={index}
                        label={benefit.replace('_', ' ')}
                        size="small"
                        sx={{ m: 0.5 }}
                      />
                    ))}

                    {selectedSubscription.can_refund_subscription && (
                      <Box sx={{ mt: 2 }}>
                        <Alert severity="info">
                          This subscription can be refunded within 30 seconds of purchase.
                        </Alert>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {selectedSubscription.benefits_used && Object.keys(selectedSubscription.benefits_used).length > 0 && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Benefits Usage History
                      </Typography>
                      <pre style={{ fontSize: '12px', backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                        {JSON.stringify(selectedSubscription.benefits_used, null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Subscription History
                    </Typography>
                    {historyLoading ? (
                      <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : subscriptionHistory.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No history entries found for this subscription yet.
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Date</strong></TableCell>
                            <TableCell><strong>Event</strong></TableCell>
                            <TableCell><strong>Plan</strong></TableCell>
                            <TableCell><strong>Upgrade</strong></TableCell>
                            <TableCell><strong>Notes</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {subscriptionHistory.map((entry) => {
                            const upgradeText = entry.from_plan_name && entry.to_plan_name
                              ? `${entry.from_plan_name} → ${entry.to_plan_name}`
                              : entry.from_plan_name || entry.to_plan_name || '—';
                            return (
                              <TableRow key={entry.id}>
                                <TableCell>
                                  {entry.created_on
                                    ? new Date(entry.created_on).toLocaleString()
                                    : '—'}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={String(entry.event_type || entry.status || '').replace(/_/g, ' ')}
                                    size="small"
                                    color={
                                      entry.event_type === 'upgrade' || entry.event_type === 'upgraded'
                                        ? 'info'
                                        : 'default'
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  {entry.subscription_plan?.plan_name || '—'}
                                </TableCell>
                                <TableCell>{upgradeText}</TableCell>
                                <TableCell>{entry.notes || '—'}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={growthDialogActionsSx}>
          <GrowthSecondaryButton onClick={handleCloseDialog}>Close</GrowthSecondaryButton>
          {selectedSubscription && (
            <>
              {selectedSubscription.status === 'active' && (
                <Button
                  color="warning"
                  variant="outlined"
                  disabled={statusUpdatingId === selectedSubscription.id}
                  startIcon={statusUpdatingId === selectedSubscription.id ? <CircularProgress size={16} /> : null}
                  onClick={() => handleStatusChange(selectedSubscription.id, 'suspended')}
                  sx={{ fontFamily: GROWTH_FONT, textTransform: 'none', borderRadius: '12px', fontWeight: 600 }}
                >
                  {statusUpdatingId === selectedSubscription.id ? 'Updating…' : 'Suspend'}
                </Button>
              )}
              {selectedSubscription.status === 'suspended' && (
                <GrowthPrimaryButton
                  disabled={statusUpdatingId === selectedSubscription.id}
                  startIcon={statusUpdatingId === selectedSubscription.id ? <CircularProgress size={16} color="inherit" /> : null}
                  onClick={() => handleStatusChange(selectedSubscription.id, 'active')}
                >
                  {statusUpdatingId === selectedSubscription.id ? 'Updating…' : 'Activate'}
                </GrowthPrimaryButton>
              )}
              {selectedSubscription.can_refund_subscription && (
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => handleRefund(selectedSubscription.id)}
                  sx={{ fontFamily: GROWTH_FONT, textTransform: 'none', borderRadius: '12px', fontWeight: 600 }}
                >
                  Process Refund
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
